import type { RosterPlayerListItem } from '../../../team/player/mapRosterPlayerToForm'
// Untyped JS helper, shared with the legacy roster tables. Rewrites the asset
// host internally — see `imageOf` below.
import { getImageThumbnailId } from '../../../../reducers/helpers'
import { toDateOnly } from '../../../profile/player/mapPlayerToForm'
import {
  toPlayerStatus,
  type PlayerStatus,
} from '../../../team/player/utils/playerStatus'

/** Invite state of a roster member, derived from `is_invited` / `is_accepted`. */
export type InviteStatus = 'accepted' | 'invited' | 'none'

export interface RosterPlayerRow {
  id: string
  /** "First Last", collapsed so a missing half never leaves a stray space. */
  name: string
  firstName: string
  lastName: string
  /** Two-letter monogram, shown only when there is no image. */
  initials: string
  /** Resolved thumbnail URL; empty when the member has no image. */
  imageUrl: string
  jersey: string
  /** Position id as a string, matching the form's `positionId`. */
  positionId: string
  email: string
  inviteStatus: InviteStatus
  isAffiliate: boolean
  isInjured: boolean
  /** Signed the waiver — the Signed Waiver column (Yes / No). */
  signedWaiver: boolean
  /** Date of birth as YYYY-MM-DD, '' when unset — the Team screen's format. */
  birthDate: string
  /** The Status column: suspended / injured / suspension ended, or none. */
  status: PlayerStatus | null
  invitationId: string | null
  /** The org's own member id, shown under the name on the Team screen; '' when unset. */
  customerIdentifier: string
}

export interface RosterOfficialRow {
  id: string
  name: string
  initials: string
  imageUrl: string
  /** Scalar id, unlike a player's `player_type.id` object. */
  officialTypeId: string
  email: string
  phone: string
  inviteStatus: InviteStatus
  invitationId: string | null
}

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

/**
 * `is_accepted` wins over `is_invited` — an accepted member is still flagged as
 * invited, and reporting them as merely "invited" would offer a pointless
 * resend.
 */
export const toInviteStatus = (member: {
  is_invited?: boolean | null
  is_accepted?: boolean | null
}): InviteStatus => {
  if (member.is_accepted) return 'accepted'
  if (member.is_invited) return 'invited'
  return 'none'
}

/** Resend is only meaningful for someone invited who has not yet accepted. */
export const canResendInvite = (status: InviteStatus): boolean =>
  status === 'invited'

/**
 * Family Management is only meaningful once the player has accepted their
 * invitation — before that there is no user account to attach family contacts
 * to. Matches the Team screen's gate (`PlayerSection.tsx`) exactly, so the two
 * surfaces cannot offer the action on different rows.
 *
 * Team officials are deliberately not covered: every roster-scoped family route
 * is `/rosters/:roster/players/:id/family`, so an official id would 404.
 */
export const canManageFamily = (
  status: InviteStatus,
  canAdmin: boolean
): boolean => canAdmin && status === 'accepted'

/**
 * The email belongs to the player once they accept, so it stops being a roster
 * field an admin may overwrite. Until then it is how the invite reaches them
 * and stays editable.
 */
export const canEditPlayerEmail = (status: InviteStatus): boolean =>
  status !== 'accepted'

export const initialsOf = (first: string, last: string): string => {
  const letters = `${first.trim()[0] ?? ''}${last.trim()[0] ?? ''}`
  return letters.toUpperCase()
}

const fullName = (first: string, last: string): string =>
  [first.trim(), last.trim()].filter(Boolean).join(' ')

/**
 * Thumbnail for a roster member.
 *
 * `getImageThumbnailId` calls `rewriteAssetHost` from `utils/assetUrl` under the
 * hood — the same rewrite `resolveAssetUrl` performs — so the S3 host still
 * becomes the active CDN and no raw API URL is rendered. It is preferred here
 * because it also picks the smallest resized format (this is a 26px avatar,
 * and `full_path` can be a multi-megabyte original) and handles the `images[]`
 * array shape, neither of which `resolveAssetUrl` does. It is what the legacy
 * roster tables use, so both surfaces resolve identically.
 *
 * Returns '' when there is no image, letting the cell fall back to initials.
 */
const imageOf = (member: unknown): string => str(getImageThumbnailId(member))

export const toRosterPlayerRow = (
  player: RosterPlayerListItem & Record<string, unknown>
): RosterPlayerRow => {
  const firstName = str(player.name_first)
  const lastName = str(player.name_last)
  const inviteStatus = toInviteStatus(player)

  return {
    id: str(player.id),
    name: fullName(firstName, lastName),
    firstName,
    lastName,
    initials: initialsOf(firstName, lastName),
    imageUrl: imageOf(player),
    jersey: str(player.player_number),
    positionId: str(player.player_type?.id),
    email: str(player.email),
    inviteStatus,
    isAffiliate: Boolean(player.is_affiliate),
    isInjured: Boolean(player.is_injured),
    signedWaiver: Boolean(player.is_signed_waiver),
    birthDate: toDateOnly(player.birth_date),
    status: toPlayerStatus(player),
    invitationId: str(player.invitation_id) || null,
    customerIdentifier: str(player.customer_identifier),
  }
}

export const toRosterOfficialRow = (
  official: Record<string, unknown>
): RosterOfficialRow => {
  const firstName = str(official.name_first)
  const lastName = str(official.name_last)

  return {
    id: str(official.id),
    name: fullName(firstName, lastName) || str(official.name_full),
    initials: initialsOf(firstName, lastName),
    imageUrl: imageOf(official),
    officialTypeId: str(official.team_official_type),
    email: str(official.email),
    phone: str(official.phone_number),
    inviteStatus: toInviteStatus(
      official as { is_invited?: boolean; is_accepted?: boolean }
    ),
    invitationId: str(official.invitation_id) || null,
  }
}
